/**
 * DatePickerDE — Lokalisierter Date-Picker mit DREI Eingabe-Wegen:
 *
 *   1. Direkte Texteingabe "TT.MM.JJJJ" — fuer schnelle Geburtsdaten.
 *      Auto-Punkt-Insertion: "1503" → "15.03." → "15.03.1985".
 *   2. Kalender-Popover mit Monats-/Jahres-Dropdown — schnelle Navigation
 *      fuer alte Daten (Geburtstag vor 30+ Jahren).
 *   3. Ein-Klick "X" zum Loeschen.
 *
 * Wert wird intern als ISO 'YYYY-MM-DD' gehalten (Backend-Format),
 * Anzeige erfolgt als 'TT.MM.JJJJ'.
 */
import React, { useEffect, useRef, useState } from 'react';
import { format, parseISO, isValid, parse } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from './button';
import { Input } from './input';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar as CalendarIcon, X } from 'lucide-react';

const ISO_FMT = 'yyyy-MM-dd';
const DE_FMT = 'dd.MM.yyyy';

const toDate = (iso) => {
  if (!iso) return undefined;
  const d = parseISO(iso);
  return isValid(d) ? d : undefined;
};

export const isoToDe = (iso) => {
  const d = toDate(iso);
  return d ? format(d, DE_FMT, { locale: de }) : '';
};

/**
 * Auto-Punkt: Roh-Eingabe "1503" → "15.03.", "15031985" → "15.03.1985".
 * Fuegt Punkte progressiv ein, egal ob der User schon Punkte gesetzt hat.
 */
const autoFormat = (raw) => {
  // Alle Punkte/Sonstiges raus, nur Ziffern behalten — max 8
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  let result = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += '.';
    result += digits[i];
  }
  return result;
};

const parseDe = (text) => {
  if (!text) return null;
  const d = parse(text, DE_FMT, new Date());
  return isValid(d) ? d : null;
};

export const DatePickerDE = ({
  value,
  onChange,
  placeholder = 'TT.MM.JJJJ',
  disabled = false,
  clearable = true,
  className = '',
  testId,
  min,
  max,
  fromYear = 1940,
  toYear,
  defaultMonth,
  showToday = true,
}) => {
  const selected = toDate(value);
  const minDate = toDate(min);
  const maxDate = toDate(max);
  const currentYear = new Date().getFullYear();
  const effectiveToYear = toYear ?? currentYear + 5;
  // Ohne Wert öffnet der Kalender standardmäßig beim heutigen Monat.
  // Felder wie Geburtsdatum können via `defaultMonth` ein früheres Startjahr setzen.
  const fallbackMonth = toDate(defaultMonth) || new Date();

  const [text, setText] = useState(() => isoToDe(value));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const inputRef = useRef(null);

  // Externer Wert -> Text
  useEffect(() => {
    setText(isoToDe(value));
  }, [value]);

  const isOutOfRange = (d) => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const commitText = (raw) => {
    const formatted = raw.trim();
    if (!formatted) {
      onChange('');
      return;
    }
    const d = parseDe(formatted);
    if (d && !isOutOfRange(d)) {
      onChange(format(d, ISO_FMT));
    } else {
      // Ungueltig -> auf zuletzt validem Wert zuruecksetzen
      setText(isoToDe(value));
    }
  };

  const handleTextChange = (e) => {
    setText(autoFormat(e.target.value));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitText(text);
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setText(isoToDe(value));
      inputRef.current?.blur();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setText('');
    onChange('');
  };

  return (
    <div className={`relative flex items-stretch ${className}`}>
      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        onChange={handleTextChange}
        onBlur={() => commitText(text)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className="pr-16 font-mono tabular-nums tracking-wide"
      />
      {clearable && selected && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          tabIndex={-1}
          className="absolute right-9 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            tabIndex={-1}
            className="absolute right-0 top-0 h-full w-9 hover:bg-muted"
            data-testid={testId ? `${testId}-calendar-trigger` : undefined}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected || fallbackMonth}
            onSelect={(d) => {
              if (d && !isOutOfRange(d)) {
                onChange(format(d, ISO_FMT));
                setPopoverOpen(false);
              }
            }}
            locale={de}
            disabled={isOutOfRange}
            captionLayout="dropdown"
            fromYear={fromYear}
            toYear={effectiveToYear}
            initialFocus
          />
          {showToday && (
            <div className="flex items-center justify-between gap-2 border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                disabled={isOutOfRange(new Date())}
                onClick={() => {
                  const t = new Date();
                  if (!isOutOfRange(t)) {
                    onChange(format(t, ISO_FMT));
                    setPopoverOpen(false);
                  }
                }}
                data-testid={testId ? `${testId}-today` : undefined}
              >
                Heute
              </Button>
              {clearable && selected && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => { onChange(''); setPopoverOpen(false); }}
                >
                  Löschen
                </Button>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
